// Import custom error classes
import errorCodes from '@/constants/errorCodes'
import UserAccountExpiredException from '@/errors/UserAccountExpiredException'
import InvalidRequestDataException from '@/errors/InvalidRequestDataException'
import TokenMalformedException from '@/errors/TokenMalformedError'
import TokenExpiredException from '@/errors/TokenExpiredException'
import TokenInvalidException from '@/errors/TokenInvalidException'
import InvalidOtpException from '@/errors/InvalidOtpException'
import ExpiredOtpException from '@/errors/ExpiredOtpException'
import { AxiosError } from 'axios'
import { appRouter } from '@/router'
import appConfig from '@/constants/appConfig'
import ForbiddenPortalRequested from '@/errors/ForbiddenPortalRequestedException'
import EntityDoesNotExistException from '@/errors/EntityDoesNotExistException'
import EntityAlreadyExistsException from '@/errors/EntityAlreadyExistsException'
import InvalidCustomAttributeOperationException from '@/errors/InvalidCustomAttributeOperationException'
import InvalidBusinessProfileRequestedError from '@/errors/InvalidBusinessProfileRequestedError'

// Global error handler
export const handleError = (error: AxiosError) => {
  if (error.response) {
    // Extract error code from the response
    const errorData = extractErrorData(error)

    // Switch case for different error codes
    switch (errorData.errorCode) {
      case errorCodes.UserAccountExpiredException:
        return Promise.reject(new UserAccountExpiredException(error.message))
      case errorCodes.InvalidRequestDataException:
      case errorCodes.HttpBadRequest:
        return Promise.reject(
          new InvalidRequestDataException(errorData.message || error.message)
        )
      case errorCodes.TokenMalformedException:
      case errorCodes.TokenInvalidException:
        handleInvalidRequest(error)
        return Promise.reject(new TokenMalformedException(error.message))
      case errorCodes.TokenExpiredException:
      case errorCodes.RefreshTokenExpiredException:
        handleInvalidRequest(error)
        return Promise.reject(new TokenExpiredException(error.message))
      case errorCodes.HttpForbidden:
      case errorCodes.ForbiddenBusinessProfileRequested:
      case errorCodes.InvalidBusinessProfileRequested:
        return Promise.reject(
          new InvalidBusinessProfileRequestedError(error.message)
        )
      case errorCodes.Unauthorized:
        handleInvalidRequest(error)
        return Promise.reject(new TokenInvalidException(error.message))
      case errorCodes.ForbiddenPortalRequested:
      case errorCodes.InvalidPortalRequestedException:
        handleInvalidRequest(error)
        return Promise.reject(new ForbiddenPortalRequested(error.message))
      case errorCodes.InvalidOtpException:
        return Promise.reject(new InvalidOtpException(error.message))
      case errorCodes.ExpiredOtpException:
        return Promise.reject(new ExpiredOtpException(error.message))
      case errorCodes.EntityDoesNotExistException:
      case errorCodes.HttpEntityNotFound:
        return Promise.reject(new EntityDoesNotExistException(error.message))
      case errorCodes.EntityAlreadyExistsError:
        return Promise.reject(
          new EntityAlreadyExistsException(errorData.message || error.message)
        )
      case errorCodes.InvalidCustomAttributeOperationException:
        return Promise.reject(
          new InvalidCustomAttributeOperationException(
            errorData.message || error.message
          )
        )
      default:
        return Promise.reject(error)
    }
  } else {
    return Promise.reject(error)
  }
}
const unauthenticatedApiEndpoints = ['/v1.0/portals']

const accessErrors = new Set<number | undefined>([
  errorCodes.ForbiddenBusinessProfileRequested,
  errorCodes.HttpForbidden,
  errorCodes.Unauthorized,
  errorCodes.ForbiddenPortalRequested,
  errorCodes.InvalidPortalRequestedException,
  errorCodes.InvalidBusinessProfileRequested
])

async function handleInvalidRequest(error: AxiosError) {
  if (!appRouter) return
  const requestUrl = error?.config?.url
  const errorCode = extractErrorData(error).errorCode

  if (
    requestUrl &&
    (unauthenticatedApiEndpoints.includes(requestUrl) ||
      accessErrors.has(errorCode))
  ) {
    appRouter.push({
      name: 'NotFound',
      params: { pathMatch: appRouter.currentRoute.value.path },
      replace: true
    })
    return
  }
  const portalDomain =
    import.meta.env.MODE === 'development'
      ? 'localhost'
      : window.location.hostname

  localStorage.removeItem('accessToken')
  // Manually delete cookies by setting them to expire in the past
  document.cookie = `accessToken=; Path=/; Domain=${portalDomain}; Expires=Thu, 01 Jan 1970 00:00:00 UTC;`
  document.cookie = `user=; Path=/; Domain=${portalDomain}; Expires=Thu, 01 Jan 1970 00:00:00 UTC;`

  const loginRoute = {
    path: import.meta.env.VITE_APP_LOGIN_URL
  }
  const decodedRedirect =
    window.location.pathname.replace('/', '') ?? appConfig.LOGIN_REDIRECT_PATH
  const encodedRedirect = decodedRedirect
    ? encodeURIComponent(decodedRedirect.toString())
    : ''
  window.location.href = `${loginRoute.path}?redirect=${encodedRedirect}`
}

function extractErrorCode(data: unknown, status?: number): number | undefined {
  if (data && typeof data === 'object' && 'errorCode' in data) {
    const { errorCode } = data as { errorCode: unknown }
    if (typeof errorCode === 'number') return errorCode
  }
  return status
}

function extractErrorMessage(data: unknown): string {
  if (data && typeof data === 'object' && 'message' in data) {
    const { message } = data as { message: unknown }
    if (typeof message === 'string') return message
  }
  if (typeof data === 'string') return data
  return 'Something went wrong. Please try again or contact support.'
}

function extractErrorData(error: AxiosError) {
  const rawData = error.response?.data
  return {
    errorCode: extractErrorCode(rawData, error.response?.status),
    message: extractErrorMessage(rawData)
  }
}
